import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { briefBulletsLatinAmerikaService } from '@/services/briefBullets/briefBulletsLatinAmerikaService';
import { Loader2, CheckCircle, XCircle, Clock, RefreshCw, Play, Pause, Trash2, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { SupabaseStorageService } from '@/services/photo/supabaseStorageService';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PromptViewer } from './PromptViewer';
import { BRIEFR_BULLETS_PROMPTS, SCHEDULE_INFO } from '@/constants/briefBulletsPrompts';

export const BriefBulletsLatinAmerikaManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState('/images/latin-america-map.png');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const { data: taskStatus, isLoading: taskLoading } = useQuery({
    queryKey: ['brief-bullets-latin-amerika-task'],
    queryFn: () => briefBulletsLatinAmerikaService.getTaskStatus(),
    refetchInterval: 10000,
  });

  // Sync photo URL from task config
  if (taskStatus?.config && (taskStatus.config as any)?.photo_url && photoUrl !== (taskStatus.config as any).photo_url) {
    setPhotoUrl((taskStatus.config as any).photo_url);
  }

  const { data: weekStatus, isLoading: weekLoading } = useQuery({
    queryKey: ['brief-bullets-latin-amerika-week'],
    queryFn: () => briefBulletsLatinAmerikaService.getThisWeekStatus(),
    refetchInterval: 10000,
  });

  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ['brief-bullets-latin-amerika-history'],
    queryFn: () => briefBulletsLatinAmerikaService.getPublishHistory(4),
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['brief-bullets-latin-amerika-logs'],
    queryFn: () => briefBulletsLatinAmerikaService.getTaskLogs(5),
  });

  const isLoading = taskLoading || weekLoading || historyLoading || logsLoading;

  const publishMutation = useMutation({
    mutationFn: () => briefBulletsLatinAmerikaService.triggerManualPublish(),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Publisert!',
          description: 'Latin-Amerika Brief Bullets er nå publisert',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-task'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-week'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-history'] });
      } else {
        toast({
          title: 'Feil ved publisering',
          description: result.error || 'Ukjent feil',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Feil',
        description: 'Kunne ikke publisere artikkel',
        variant: 'destructive',
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) => briefBulletsLatinAmerikaService.toggleAutomaticPublishing(enable),
    onSuccess: (success, enable) => {
      if (success) {
        toast({
          title: enable ? 'Aktivert' : 'Pauset',
          description: enable 
            ? 'Automatisk publisering er nå aktivert'
            : 'Automatisk publisering er nå pauset',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-task'] });
      } else {
        toast({
          title: 'Feil',
          description: 'Kunne ikke endre status',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Feil',
        description: 'Kunne ikke endre status',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => briefBulletsLatinAmerikaService.deleteThisWeekArticle(),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Slettet!',
          description: 'Ukens Brief Bullets artikkel er slettet',
        });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-week'] });
        queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-history'] });
      } else {
        toast({
          title: 'Feil ved sletting',
          description: result.error || 'Ukjent feil',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Feil',
        description: 'Kunne ikke slette artikkel',
        variant: 'destructive',
      });
    },
  });

  const handleToggleAutoPublish = () => {
    const isActive = taskStatus?.status === 'active';
    toggleMutation.mutate(!isActive);
  };

  const handleDeleteThisWeek = () => {
    if (!confirm('Er du sikker på at du vil slette ukens Brief Bullets artikkel?')) {
      return;
    }
    deleteMutation.mutate();
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(file, 'profile-images');
      setPhotoUrl(uploadedUrl);
      
      await briefBulletsLatinAmerikaService.updatePhotoUrl(uploadedUrl);

      toast({
        title: 'Bilde lastet opp',
        description: 'Profilbildet er oppdatert',
      });
      
      queryClient.invalidateQueries({ queryKey: ['brief-bullets-latin-amerika-task'] });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke laste opp bilde',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Aktiv</Badge>;
      case 'paused':
        return <Badge variant="secondary">Pauset</Badge>;
      case 'failed':
        return <Badge variant="destructive">Feilet</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🌎 Briefr Bullets: Latin-Amerika
        </CardTitle>
        <CardDescription>
          Ukentlig nyhetsbulletin om Latin-Amerika - publiseres fredager kl. 05:30
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Prompt Viewer */}
        <PromptViewer 
          taskName="Latin-Amerika"
          prompt={BRIEFR_BULLETS_PROMPTS.latinAmerika}
          config={taskStatus?.config}
          scheduleInfo={SCHEDULE_INFO.latinAmerika}
        />

        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Status denne uken</h3>
            {weekStatus?.published ? (
              <Badge variant="default" className="bg-green-500">
                <CheckCircle className="h-3 w-3 mr-1" />
                Publisert
              </Badge>
            ) : (
              <Badge variant="secondary">
                <Clock className="h-3 w-3 mr-1" />
                Venter
              </Badge>
            )}
          </div>

          {weekStatus?.article && (
            <div className="text-sm text-muted-foreground">
              Publisert kl. {format(new Date(weekStatus.article.created_at), 'HH:mm', { locale: nb })}
            </div>
          )}

          {taskStatus?.error_message && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Siste feil:</p>
                <p>{taskStatus.error_message}</p>
                {taskStatus.retry_count > 0 && (
                  <p className="mt-1 text-xs">
                    Forsøk {taskStatus.retry_count} av {(taskStatus.config as any)?.max_retries || 1}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Task Configuration */}
        {taskStatus && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Konfigurasjon</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">{getStatusBadge(taskStatus.status)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Publiseringstid:</span>
                <div className="mt-1 font-medium">Fredager kl. {taskStatus.schedule_time?.slice(0, 5)}</div>
              </div>
              {taskStatus.next_run && (
                <div>
                  <span className="text-muted-foreground">Neste kjøring:</span>
                  <div className="mt-1 font-medium">
                    {format(new Date(taskStatus.next_run), 'dd.MM HH:mm', { locale: nb })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Upload */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Profilbilde for Kai</h3>
          <div className="flex items-center gap-3">
            <img 
              src={photoUrl} 
              alt="Kai profil" 
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
            <div className="flex-1">
              <input
                type="file"
                id="photo-upload-latin-amerika"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={isUploadingPhoto}
                onClick={() => document.getElementById('photo-upload-latin-amerika')?.click()}
              >
                {isUploadingPhoto ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Last opp nytt bilde
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Dette bildet brukes som profilbilde i Brief Bullets artiklene
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="flex-1"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiserer...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Publiser nå
                </>
              )}
            </Button>
            <Button
              onClick={() => queryClient.invalidateQueries()}
              variant="outline"
              size="icon"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleToggleAutoPublish}
              disabled={toggleMutation.isPending}
              variant={taskStatus?.status === 'paused' ? 'default' : 'secondary'}
              className="flex-1"
            >
              {toggleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Endrer...
                </>
              ) : taskStatus?.status === 'paused' ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Aktiver automatisk publisering
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause automatisk publisering
                </>
              )}
            </Button>
            <Button
              onClick={handleDeleteThisWeek}
              disabled={deleteMutation.isPending || !weekStatus?.published}
              variant="destructive"
              size="icon"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Siste publiseringer</h3>
            <div className="space-y-2">
              {history.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-card text-sm"
                >
                  <div>
                    <div className="font-medium">
                      {format(new Date(article.created_at), 'dd. MMMM yyyy', { locale: nb })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Kl. {format(new Date(article.created_at), 'HH:mm', { locale: nb })}
                    </div>
                  </div>
                  <Badge variant="default" className="bg-green-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Publisert
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Logs */}
        {logs.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Logg</h3>
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-2 p-2 rounded-lg border bg-card text-sm"
                >
                  {log.status === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : log.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{log.message}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm', { locale: nb })}
                      {log.execution_time_ms && ` • ${(log.execution_time_ms / 1000).toFixed(1)}s`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};